import { TextField, Typography } from "@king-ict/eupisi-ui/components";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { CommercialProgram } from "../../../models/Request/CommercialProgram";
import { useLazyGetInstitutionSelectionQuery } from "../../../queries";
import { RequestInfoPanel } from "../../Request/RequestComponents/RequestInfoPanel";

interface IProps {
  emptyValue: string;
  requestId: number;
  showEdit: boolean;
  moveToSpecificStep?: () => void;
}

export default function InstitutionProgramDetails({
  emptyValue,
  requestId,
  showEdit,
  moveToSpecificStep,
}: IProps) {
  const { t } = useTranslation();
  const [
    triggerInstitutionSelection,
    { data: institutionSelection, isLoading },
  ] = useLazyGetInstitutionSelectionQuery();

  const handleExpandablePanelClick = () => {
    if (!institutionSelection && !isLoading) {
      triggerInstitutionSelection(requestId);
    }
  };

  const getCommercialProgramName = (
    commercialProgramId?: CommercialProgram | null
  ): string => {
    if (!commercialProgramId) return emptyValue;
    const programKey =
      commercialProgramId === CommercialProgram.FullDayClasses
        ? "FullDayClasses"
        : "ExtendedStay";
    return t(`commercialProgram.${programKey}`);
  };

  if (!institutionSelection) {
    return (
      <RequestInfoPanel
        title={t("requestReviewStep.locationProgram")}
        subTitle={t("requestReviewStep.requestPrimaryInstitution")}
        showEdit={showEdit}
        isLoading={isLoading}
        onEditClick={moveToSpecificStep}
        onExpandablePanelClick={handleExpandablePanelClick}
      />
    );
  }

  const primaryInstitution = institutionSelection.requestInstitution;
  const secondaryInstitution =
    institutionSelection.requestOutsideEnrollmentAreaInstitution;

  return (
    <>
      <RequestInfoPanel
        title={t("requestReviewStep.locationProgram")}
        subTitle={t("requestReviewStep.requestPrimaryInstitution")}
        showEdit={showEdit}
        isLoading={isLoading}
        onEditClick={moveToSpecificStep}
        onExpandablePanelClick={handleExpandablePanelClick}
      >
        <Row>
          <Col md={4}>
            <TextField
              type="text"
              label={t("requestReviewStep.institutionName")}
              value={primaryInstitution.institutionName || emptyValue}
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              type="text"
              label={t("requestReviewStep.institutionProgram")}
              value={primaryInstitution.programName || emptyValue}
              plaintext
            />
          </Col>
          {!!primaryInstitution.languageSchoolModels.length && (
            <Col md={4}>
              <TextField
                type="text"
                label={t("institutionDataStep.schoolingModels")}
                value={primaryInstitution.languageSchoolModels
                  .map((v) => v.name)
                  .join(", ")}
                plaintext
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              type="text"
              label={t("requestReviewStep.institutionElectiveSubjects")}
              value={
                primaryInstitution.electiveSubjects
                  .map((v) => v.name)
                  .join(", ") || emptyValue
              }
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              type="text"
              label={t("institutionDataStep.foreignLanguage")}
              value={
                primaryInstitution.mandatoryForeignLanguages
                  .map((v) => v.name)
                  .join(", ") || emptyValue
              }
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              type="text"
              label={t("institutionDataStep.foreignProgramLanguage")}
              value={
                primaryInstitution.foreignLanguages
                  .map((v) => v.name)
                  .join(", ") || emptyValue
              }
              plaintext
            />
          </Col>
        </Row>
        {primaryInstitution.commercialProgramId && (
          <Row>
            <Col md={4}>
              <TextField
                type="text"
                label={t("requestReviewStep.institutionCommertialProgram")}
                value={getCommercialProgramName(
                  primaryInstitution.commercialProgramId
                )}
                plaintext
              />
            </Col>
          </Row>
        )}
        {secondaryInstitution && (
          <>
            <Typography variant="smallHeading" className="pt-3">
              {t("requestReviewStep.requestSecondaryInstitution")}
            </Typography>
            <Row>
              <Col md={4}>
                <TextField
                  type="text"
                  label={t("requestReviewStep.institutionName")}
                  value={secondaryInstitution.institutionName || emptyValue}
                  plaintext
                />
              </Col>
              <Col md={4}>
                <TextField
                  type="text"
                  label={t("requestReviewStep.institutionProgram")}
                  value={secondaryInstitution.programName || emptyValue}
                  plaintext
                />
              </Col>
              {!!secondaryInstitution.languageSchoolModels.length && (
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("institutionDataStep.schoolingModels")}
                    value={secondaryInstitution.languageSchoolModels
                      .map((v) => v.name)
                      .join(", ")}
                    plaintext
                  />
                </Col>
              )}
            </Row>
            <Row>
              <Col md={4}>
                <TextField
                  type="text"
                  label={t("requestReviewStep.institutionElectiveSubjects")}
                  value={
                    secondaryInstitution.electiveSubjects
                      .map((v) => v.name)
                      .join(", ") || emptyValue
                  }
                  plaintext
                />
              </Col>
              <Col md={4}>
                <TextField
                  type="text"
                  label={t("institutionDataStep.foreignLanguage")}
                  value={
                    secondaryInstitution.mandatoryForeignLanguages
                      .map((v) => v.name)
                      .join(", ") || emptyValue
                  }
                  plaintext
                />
              </Col>
              <Col md={4}>
                <TextField
                  type="text"
                  label={t("institutionDataStep.foreignProgramLanguage")}
                  value={
                    secondaryInstitution.foreignLanguages
                      .map((v) => v.name)
                      .join(", ") || emptyValue
                  }
                  plaintext
                />
              </Col>
            </Row>
            {secondaryInstitution.commercialProgramId && (
              <Row>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("requestReviewStep.institutionCommertialProgram")}
                    value={getCommercialProgramName(
                      secondaryInstitution.commercialProgramId
                    )}
                    plaintext
                  />
                </Col>
              </Row>
            )}
          </>
        )}
      </RequestInfoPanel>
    </>
  );
}
